import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {TIMING_TYPES} from '../../../../../../operations/components/Modals/AddAnnouncement/data';
import {
  createDefaultRegulation,
  DepartmentFormValues,
  OnboardingUserFormValues,
  RegulationFormValues,
  RoleFormValues,
} from '../../../types';
import {
  parseBooleanFromExcel,
  parseExcelSheet,
} from '../checklists/checklistExcelTemplate';
import {
  getEligibleRegulationAssigneeUsers,
  getUserDisplayName,
  matchRegulationAssigneeNameToId,
  RoleTypeOption,
} from './regulationAssigneeUtils';

export const REGULATION_EXCEL_HEADERS = {
  name: 'שם רגולציה',
  description: 'תיאור',
  department: 'מחלקה',
  assignee: 'שיוך אישי',
  type: 'סוג',
  sendNotificationBeforeDate: 'התראה לפני מועד',
  notificationTiming: 'תזמון התראה',
  schedulingType: 'סוג תזמון',
  startDate: 'תאריך התחלה',
  endDate: 'תאריך סיום',
  startTime: 'שעת התחלה',
  endTime: 'שעת סיום',
} as const;

export type RegulationExcelColumnKey = keyof typeof REGULATION_EXCEL_HEADERS;

export const REGULATION_EXCEL_COLUMNS: Array<{
  key: RegulationExcelColumnKey;
  header: string;
}> = Object.entries(REGULATION_EXCEL_HEADERS).map(([key, header]) => ({
  key: key as RegulationExcelColumnKey,
  header,
}));

const TEMPLATE_DATA_SHEET_NAME = 'תבנית';

const REGULATION_TYPE_LABELS: Record<string, string> = {
  yellow: 'צהוב',
  red: 'אדום',
};

const NOTIFICATION_TIMING_LABELS: Record<string, string> = {
  '1_day_before': 'יום לפני',
  '2_days_before': 'יומיים לפני',
  '4_days_before': '4 ימים לפני',
  '1_week_before': 'שבוע לפני',
};

export const REGULATION_TEMPLATE_SAMPLE_ROW: Record<string, string> = {
  [REGULATION_EXCEL_HEADERS.name]: 'רגולציה לדוגמה',
  [REGULATION_EXCEL_HEADERS.description]: 'תיאור לדוגמה',
  [REGULATION_EXCEL_HEADERS.department]: '',
  [REGULATION_EXCEL_HEADERS.assignee]: '',
  [REGULATION_EXCEL_HEADERS.type]: 'צהוב',
  [REGULATION_EXCEL_HEADERS.sendNotificationBeforeDate]: 'לא',
  [REGULATION_EXCEL_HEADERS.notificationTiming]: '',
  [REGULATION_EXCEL_HEADERS.schedulingType]: 'ללא תזמון - דיפולט',
  [REGULATION_EXCEL_HEADERS.startDate]: '',
  [REGULATION_EXCEL_HEADERS.endDate]: '',
  [REGULATION_EXCEL_HEADERS.startTime]: '',
  [REGULATION_EXCEL_HEADERS.endTime]: '',
};

function booleanToExcel(value: boolean): string {
  return value ? 'כן' : 'לא';
}

function sanitizeFileNamePart(value: string): string {
  return (
    value.replace(/[^\w\u0590-\u05FF-]+/g, '_').replace(/^_|_$/g, '') ||
    'Regulation'
  );
}

function getSchedulingTypeTitle(key: string): string {
  return TIMING_TYPES.find(t => t.key === key)?.title ?? key;
}

function parseSchedulingTypeFromExcel(val: unknown): string {
  const raw = String(val ?? '').trim();
  if (!raw) return 'default';
  const byTitle = TIMING_TYPES.find(t => t.title === raw);
  if (byTitle) return byTitle.key;
  const byKey = TIMING_TYPES.find(t => t.key === raw);
  return byKey?.key ?? 'default';
}

function parseRegulationTypeFromExcel(val: unknown): string {
  const raw = String(val ?? '').trim();
  if (raw === 'אדום' || raw === 'red') return 'red';
  if (raw === 'צהוב' || raw === 'yellow') return 'yellow';
  return '';
}

function parseNotificationTimingFromExcel(val: unknown): string | undefined {
  const raw = String(val ?? '').trim();
  if (!raw) return undefined;
  const entry = Object.entries(NOTIFICATION_TIMING_LABELS).find(
    ([, label]) => label === raw,
  );
  return entry?.[0] ?? undefined;
}

function matchDepartmentNameToId(
  name: string,
  nodeDepartments: DepartmentFormValues[],
): {department: string} | {error: string} {
  const trimmed = name.trim();
  if (!trimmed) return {department: ''};

  const dept = nodeDepartments.find(
    d => d.name.toLowerCase().trim() === trimmed.toLowerCase(),
  );
  if (!dept) {
    return {error: `מחלקה לא נמצאה: ${trimmed}`};
  }
  return {department: dept.id};
}

function downloadRegulationsExcelFile(
  rows: Record<string, string>[],
  fileName: string,
) {
  const dataSheet = XLSX.utils.json_to_sheet(rows);
  const colWidths = REGULATION_EXCEL_COLUMNS.map(c => ({
    wch: Math.max(c.header.length, 14),
  }));
  (dataSheet as XLSX.WorkSheet)['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, dataSheet, TEMPLATE_DATA_SHEET_NAME);

  const buf = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  saveAs(blob, fileName);
}

export function mapRegulationToExcelRow(
  regulation: RegulationFormValues,
  nodeDepartments: DepartmentFormValues[],
  nodeUsers: OnboardingUserFormValues[],
): Record<string, string> {
  const departmentName =
    nodeDepartments.find(d => d.id === regulation.department)?.name ?? '';
  const assigneeUser = nodeUsers.find(u => u.id === regulation.assignee);
  const assigneeName = assigneeUser ? getUserDisplayName(assigneeUser) : '';

  return {
    [REGULATION_EXCEL_HEADERS.name]: regulation.name,
    [REGULATION_EXCEL_HEADERS.description]:
      regulation.description?.trim() ?? '',
    [REGULATION_EXCEL_HEADERS.department]: departmentName,
    [REGULATION_EXCEL_HEADERS.assignee]: assigneeName,
    [REGULATION_EXCEL_HEADERS.type]:
      REGULATION_TYPE_LABELS[regulation.type] ?? regulation.type,
    [REGULATION_EXCEL_HEADERS.sendNotificationBeforeDate]: booleanToExcel(
      regulation.sendNotificationBeforeDate,
    ),
    [REGULATION_EXCEL_HEADERS.notificationTiming]: regulation.notificationTiming
      ? (NOTIFICATION_TIMING_LABELS[regulation.notificationTiming] ?? '')
      : '',
    [REGULATION_EXCEL_HEADERS.schedulingType]: getSchedulingTypeTitle(
      regulation.schedulingType,
    ),
    [REGULATION_EXCEL_HEADERS.startDate]: regulation.startDate ?? '',
    [REGULATION_EXCEL_HEADERS.endDate]: regulation.endDate ?? '',
    [REGULATION_EXCEL_HEADERS.startTime]: regulation.startTime ?? '',
    [REGULATION_EXCEL_HEADERS.endTime]: regulation.endTime ?? '',
  };
}

export function downloadOnboardingRegulationExcelExport(
  regulation: RegulationFormValues,
  nodeDepartments: DepartmentFormValues[],
  nodeUsers: OnboardingUserFormValues[],
  options?: {fileName?: string},
) {
  const row = mapRegulationToExcelRow(
    regulation,
    nodeDepartments,
    nodeUsers,
  );
  const namePart = sanitizeFileNamePart(regulation.name);

  downloadRegulationsExcelFile(
    [row],
    options?.fileName ?? `Regulation_${namePart}.xlsx`,
  );
}

function getWorksheetForImport(workbook: XLSX.WorkBook): XLSX.WorkSheet | null {
  const sheetName =
    workbook.SheetNames.find(name => name === TEMPLATE_DATA_SHEET_NAME) ??
    workbook.SheetNames[0];
  if (!sheetName) return null;
  return workbook.Sheets[sheetName] ?? null;
}

export function isValidOnboardingRegulationExcelData(
  jsonData: Record<string, unknown>[],
): boolean {
  if (!jsonData.length) return false;
  const keys = Object.keys(jsonData[0]);
  return (
    keys.includes(REGULATION_EXCEL_HEADERS.name) &&
    keys.includes(REGULATION_EXCEL_HEADERS.type)
  );
}

function isTemplateSampleRow(row: Record<string, unknown>): boolean {
  const name = String(row[REGULATION_EXCEL_HEADERS.name] ?? '').trim();
  return name === REGULATION_TEMPLATE_SAMPLE_ROW[REGULATION_EXCEL_HEADERS.name];
}

export function mapExcelRowToRegulation(
  row: Record<string, unknown>,
  nodeDepartments: DepartmentFormValues[],
  nodeUsers: OnboardingUserFormValues[],
  nodeRoles: RoleFormValues[],
  allRoleTypes: {data?: RoleTypeOption[]} | undefined,
  folderId: string,
): {regulation: RegulationFormValues} | {error: string} {
  const name = String(row[REGULATION_EXCEL_HEADERS.name] ?? '').trim();
  if (name.length < 1) {
    return {error: 'שם רגולציה חובה'};
  }

  const type = parseRegulationTypeFromExcel(row[REGULATION_EXCEL_HEADERS.type]);
  if (!type) {
    return {error: 'סוג רגולציה לא תקין (צהוב / אדום)'};
  }

  const departmentRaw = String(
    row[REGULATION_EXCEL_HEADERS.department] ?? '',
  ).trim();
  let department = '';
  if (departmentRaw) {
    const deptMatch = matchDepartmentNameToId(departmentRaw, nodeDepartments);
    if ('error' in deptMatch) {
      return {error: deptMatch.error};
    }
    department = deptMatch.department;
  }

  const assigneeRaw = String(row[REGULATION_EXCEL_HEADERS.assignee] ?? '').trim();
  let assignee = '';
  if (assigneeRaw) {
    const eligibleAssignees = getEligibleRegulationAssigneeUsers(
      nodeUsers,
      nodeRoles,
      allRoleTypes,
      department || undefined,
    );
    const assigneeMatch = matchRegulationAssigneeNameToId(
      assigneeRaw,
      eligibleAssignees,
    );
    if ('error' in assigneeMatch) {
      return {error: assigneeMatch.error};
    }
    assignee = assigneeMatch.assignee;
  }

  const sendNotificationBeforeDate = parseBooleanFromExcel(
    row[REGULATION_EXCEL_HEADERS.sendNotificationBeforeDate],
    false,
  );

  const notificationTiming = sendNotificationBeforeDate
    ? parseNotificationTimingFromExcel(
        row[REGULATION_EXCEL_HEADERS.notificationTiming],
      )
    : undefined;

  const regulation = createDefaultRegulation({
    name,
    description: String(row[REGULATION_EXCEL_HEADERS.description] ?? '').trim(),
    department,
    assignee,
    type,
    sendNotificationBeforeDate,
    notificationTiming,
    schedulingType: parseSchedulingTypeFromExcel(
      row[REGULATION_EXCEL_HEADERS.schedulingType],
    ),
    startDate: String(row[REGULATION_EXCEL_HEADERS.startDate] ?? '').trim(),
    endDate: String(row[REGULATION_EXCEL_HEADERS.endDate] ?? '').trim(),
    startTime: String(row[REGULATION_EXCEL_HEADERS.startTime] ?? '').trim(),
    endTime: String(row[REGULATION_EXCEL_HEADERS.endTime] ?? '').trim(),
    folderId,
  });

  return {regulation};
}

export type ParseOnboardingRegulationExcelResult = {
  regulations: RegulationFormValues[];
  rowErrors: string[];
  skippedSampleRows: number;
};

export type RegulationExcelImportContext = {
  nodeDepartments: DepartmentFormValues[];
  nodeUsers: OnboardingUserFormValues[];
  nodeRoles: RoleFormValues[];
  allRoleTypes: {data?: RoleTypeOption[]} | undefined;
  folderId: string;
};

export function parseOnboardingRegulationExcelWorkbook(
  workbook: XLSX.WorkBook,
  {
    nodeDepartments,
    nodeUsers,
    nodeRoles,
    allRoleTypes,
    folderId,
  }: RegulationExcelImportContext,
): ParseOnboardingRegulationExcelResult {
  const worksheet = getWorksheetForImport(workbook);
  if (!worksheet) {
    return {
      regulations: [],
      rowErrors: ['הקובץ ריק או לא תקין'],
      skippedSampleRows: 0,
    };
  }

  const jsonData = parseExcelSheet(worksheet);
  if (!isValidOnboardingRegulationExcelData(jsonData)) {
    return {
      regulations: [],
      rowErrors: ['פורמט קובץ לא תקין. השתמשו בתבנית שהורדה מהמערכת'],
      skippedSampleRows: 0,
    };
  }

  const regulations: RegulationFormValues[] = [];
  const rowErrors: string[] = [];
  let skippedSampleRows = 0;

  jsonData.forEach((row, index) => {
    const name = String(row[REGULATION_EXCEL_HEADERS.name] ?? '').trim();
    if (!name) return;

    if (isTemplateSampleRow(row)) {
      skippedSampleRows += 1;
      return;
    }

    const result = mapExcelRowToRegulation(
      row,
      nodeDepartments,
      nodeUsers,
      nodeRoles,
      allRoleTypes,
      folderId,
    );
    if ('error' in result) {
      rowErrors.push(`שורה ${index + 2}: ${result.error}`);
      return;
    }

    regulations.push(result.regulation);
  });

  return {regulations, rowErrors, skippedSampleRows};
}

export async function readOnboardingRegulationExcelFile(
  file: File,
  importContext: RegulationExcelImportContext,
): Promise<ParseOnboardingRegulationExcelResult> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), {type: 'array'});
  return parseOnboardingRegulationExcelWorkbook(workbook, importContext);
}
